import React, { useState, useEffect, Fragment, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { useParams, useNavigate } from "react-router-dom";
import { Breadcrumbs } from "components/navigation";
import { CircleStatus } from "components/status";
import { NotificationToaster, AlertConfirm, AlertUnsavedData, NotificationInline } from "components/notifications";
import { Intent } from "@blueprintjs/core";
import { UserAccess, UserHasAccess } from "components/user-access";
import { PageRestricted } from "pages/errors/page-restricted";
import { CheckService } from "services/CheckService";
import { Button, ButtonSave, LinkButton } from "components/buttons";
import { HierarchyQuestion, HierarchySection } from "components/hierarchy";

import "components/hierarchy/Hierarchy.css";
import "./TemplateBuilder.css";
import { ShowHide } from "components/layout";
import axios from "axios";
import { HelperFunctions } from "helpers";
import { useDirtyData } from "hooks/useDirtyData";
import classNames from "classnames";
import { TemplateBuilderPreview } from "pages/checks/templates/builder/preview";
import { TemplateStatuses, TemplateTypes } from "pages/checks/constants";
import { DndContext, useDroppable } from "@dnd-kit/core";
import { StringToEllipsesString } from "components/formatting";
import { useTemplateConfig } from "pages/checks/hooks/useTemplateConfig";
import { AutoSave } from "components/auto-save/AutoSave";
import { FormTextInput } from "components/form-fields";
import { Tooltip } from "components/tooltip";
import { useTemplateContext } from "pages/checks/context";
import { v4 as uuidv4 } from 'uuid';

export function TemplateBuilder(props) {
    var navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [archiving, setArchiving] = useState(false);
    const [versioning, setVersioning] = useState(false);
    const [template, setTemplate] = useState({
        status: "",
        name: "",
        versionNumber: 1,
        type: "",
        subCategory: ""
    });
    const [statusColour, setStatusColour] = useState("");

    const defaultReference = uuidv4();
    const [layout, setLayout] = useState({
        pages: [
            {
                id: -1,
                title: "Page",
                helperText: null,
                componentNumber: 1,
                internalReference: defaultReference,
                childComponents: [
                ]
            }
        ]
    });
    const [originalLayout, setOriginalLayout] = useState({
        pages: [
            {
                id: -1,
                title: "Page",
                helperText: null,
                internalReference: defaultReference,
                componentNumber: 1,
                childComponents: [
                ]
            }
        ]
    });
    const [componentToDelete, setComponentToDelete] = useState(null);
    const [showAddPageConfirm, setShowAddPageConfirm] = useState(false);
    const [showArchiveTemplateConfirm, setShowArchiveTemplateConfirm] = useState(false);
    const [showPublishTemplateConfirm, setShowPublishTemplateConfirm] = useState(false);
    const [showNewVersionConfirm, setShowNewVersionConfirm] = useState(false);
    const [isLatestVersion, setIsLatestVersion] = useState(false);
    const { useGlobalEndpoint, requiredAction, baseRoute, canEditGlobal } = useTemplateConfig(props.useGlobalConfig);
    const [templateMedia, setTemplateMedia] = useState([]);
    const [componentInEditMode, setComponentInEditMode] = useState(null);
    const [componentBeingMoved, setComponentBeingMoved] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [templateOptions, setTemplateOptions] = useState({
        questionTypes: [],
        templateCategories: []
    });

    const { addedMedia, removedMedia } = useTemplateContext();
    const [oldLayout, setOldLayout] = useState(null);
    const [componentsWithInvalidConditionsAfterMove, setComponentsWithInvalidConditionsAfterMove] = useState([]);

    const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
    const [isAutoSaving, setIsAutoSaving] = useState(false);
    const [lastSaveTime, setLastSaveTime] = useState(null);
    const needToAutoSaveAgain = useRef(false);
    const [layoutToAutoSave, setLayoutToAutoSave] = useState(null);

    const [componentToClone, setComponentToClone] = useState(null);
    const [nameOfClonedComponent, setNameOfClonedComponent] = useState(null);

    const internalReferencesToServerIdsMap = useRef({});

    const userIsSuperInternal = UserHasAccess("Internal:Super");

    const isReadOnly = useMemo(() => {
        if (template) {
            return template.status !== TemplateStatuses.DRAFT;
        }

        return false;
    }, [template]);

    const questionTypes = useMemo(() => {
        if (template && templateOptions) {
            return templateOptions.questionTypes
                .filter(x => !x.subCategory || x.subCategory === template.subCategory)
        }

        return [];
    }, [templateOptions, template]);

    const scrollTo = useRef();

    const showLoadingState = useMemo(() => loading || saving || archiving || versioning, [loading, saving, archiving, versioning]);

    const isLayoutDirty = useDirtyData(originalLayout, layout);

    const isSectionOrPageComponent = (component) => component.$componentType === "StandardContainer" || !component.$componentType;
    const isQuestionComponent = (component) => component.$componentType !== "StandardContainer" && !!component.$componentType;
    const isPageComponent = (component) => !!component && !component.$componentType;

    const getTypeOfComponent = (component) => {
        if (!component) {
            return "";
        }

        if (isPageComponent(component)) {
            return "page";
        } else if (isQuestionComponent(component)) {
            return "question";
        } else {
            return "section";
        }
    }

    const triggerQuestionTypes = ["YesNoQuestion", "DropdownQuestion", "MultiSelectQuestion"];

    const flattenComponent = (component) => {
        if (isSectionOrPageComponent(component)) {
            const flattened = [component];
            for (const child of component.childComponents) {
                const flattenedChild = flattenComponent(child);
                flattenedChild.forEach(c => flattened.push(c));
            }

            return flattened;
        } else {
            return [component];
        }
    }

    const flattenLayout = (layoutToFlatten) => {
        const allComponents = [];

        for (const page of layoutToFlatten.pages) {
            flattenComponent(page).forEach(c => allComponents.push(c));
        }

        return allComponents;
    };

    const getRepeatableParentIdsOfComponent = (component, allComponents) => getParentsOfComponent(component, allComponents).filter(c => c.repeatability).map(i => i.id);

    const getParentsOfComponent = (component, allComponents) => {
        const parents = [];
        let currentComponent = component;
        const allSections = allComponents.filter(l => isSectionOrPageComponent(l));

        while (!isPageComponent(currentComponent)) {
            const newParent = allSections.find(pageItem => pageItem.childComponents.some(c => c.id === currentComponent.id && c.$componentType === currentComponent.$componentType));
            currentComponent = newParent;
            parents.push(newParent);
        }

        return parents;
    }

    const flattenedLayout = useMemo(() => flattenLayout(layout), [layout]);

    const parentOfComponentToDelete = useMemo(() => {
        if (!componentToDelete || !componentToDelete.$componentType) {
            return null;
        } else {
            return flattenedLayout.find(s => isSectionOrPageComponent(s) && s.childComponents.some(c => c.id === componentToDelete.id && c.$componentType === componentToDelete.$componentType));
        }
    }, [componentToDelete, flattenedLayout]);

    const nextUniqueId = useMemo(() => {
        const nextId = Math.min(...flattenedLayout.map(x => x.id)) - 1;
        return nextId < 0 ? nextId : -1; // New IDS must be negative
    }, [flattenedLayout]);

    const getComponentsWhereTriggerQuestionIsWithinComponent = (component) => {
        if (!component) {
            return [];
        }

        const allComponentsWithConditions = flattenedLayout.filter(c => !!c.condition);
        const allComponentsWithinEditableComponent = flattenComponent(component);
        const allQuestionsWithinComponent = allComponentsWithinEditableComponent
            .filter(c => isQuestionComponent(c));

        return allComponentsWithConditions
            .filter(c => allQuestionsWithinComponent.some(q => q.id === c.condition.triggerQuestionId));
    }

    const componentsWhereTriggerQuestionIsWithinDeletableComponent = useMemo(
        () => getComponentsWhereTriggerQuestionIsWithinComponent(componentToDelete),
        [componentToDelete, flattenedLayout]);

    const getConditionsWhereQuestionIsTrigger = (question, allComponents) => {
        if (!question || !isQuestionComponent(question)) {
            return [];
        }

        return allComponents.filter(x => !!x.condition)
            .map(x => x.condition)
            .filter(x => x.triggerQuestionId === question.id);
    }

    const conditionsWhereTriggerQuestionIsCurrentEditableComponent = useMemo(() => getConditionsWhereQuestionIsTrigger(componentInEditMode, flattenedLayout), [componentInEditMode, flattenedLayout]);

    const reasonWhyComponentCannotBeRepeatable = useMemo(() => {
        if (!componentInEditMode || isPageComponent(componentInEditMode)) {
            return "Pages cannot be made repeatable.";
        }

        const allComponentsWithConditions = flattenedLayout.filter(c => !!c.condition);

        if (isQuestionComponent(componentInEditMode)) {
            const anyConditionsForQuestion = allComponentsWithConditions.map(c => c.condition).some(c => c.triggerQuestionId === componentInEditMode.id);
            return anyConditionsForQuestion ? "This question is a trigger question for one or more conditions." : "";
        } else {
            const allComponentsWithinEditableComponent = flattenComponent(componentInEditMode);
            const componentsWhereTriggerQuestionIsWithinEditableComponent = getComponentsWhereTriggerQuestionIsWithinComponent(componentInEditMode);

            if (!componentsWhereTriggerQuestionIsWithinEditableComponent.length) {
                return "";
            }

            const sectionCannotBeRepeatable = componentsWhereTriggerQuestionIsWithinEditableComponent
                .some(c => !allComponentsWithinEditableComponent.includes(c));

            if (sectionCannotBeRepeatable) {
                return "Making this section repeatable would invalidate one or more conditions.";
            }

            return "";
        }
    }, [componentInEditMode]);

    const getPottentialTriggerQuestions = (component, flattenedLayoutToConsider) => {
        if (!component) {
            return [];
        }

        const indexOfEditedComponent = flattenedLayoutToConsider.indexOf(component);
        const possibleQuestions = flattenedLayoutToConsider.filter((x, i) => triggerQuestionTypes.includes(x.$componentType) && !x.repeatability && i < indexOfEditedComponent);
        const repeatableParentsOfEditedComponent = getRepeatableParentIdsOfComponent(component, flattenedLayoutToConsider);
        const validQuestions = [];

        for (const question of possibleQuestions) {
            const repeatableParentsOfQuestion = getRepeatableParentIdsOfComponent(question, flattenedLayoutToConsider);
            const firstRepeatableParentOfQuestion = repeatableParentsOfQuestion.length ? repeatableParentsOfQuestion[0] : null;

            if (!firstRepeatableParentOfQuestion || repeatableParentsOfEditedComponent.includes(firstRepeatableParentOfQuestion)) {
                validQuestions.push(question);
            }
        }

        return validQuestions;
    }

    const pottentialTriggerQuestions = useMemo(() => getPottentialTriggerQuestions(componentInEditMode, flattenedLayout), [componentInEditMode]);

    const conditionalHelperText = useMemo(() => {
        if (pottentialTriggerQuestions.length) {
            return null;
        }

        const typeOfComponent = getTypeOfComponent(componentInEditMode);

        return <div className="include-tooltip-bullet-points">
            <p>
                A {typeOfComponent} can only have a condition if there is a valid trigger question for it. A trigger question must comply with the following:
            </p>

            <ul>
                <li>It must be one of the following types:
                    <ul>
                        {questionTypes.filter(x => triggerQuestionTypes.includes(`${x.key}Question`)).map(x => <li key={x.name}>{x.name}</li>)}
                    </ul>
                </li>
                <li>It must be before this {typeOfComponent} in the layout.</li>
                <li>It cannot be possible to repeat the trigger question more times than this {typeOfComponent}.</li>
            </ul>
        </div>
    }, [pottentialTriggerQuestions, componentInEditMode]);

    useEffect(() => {
        setTimeout(() => {
            if (scrollTo.current) {
                scrollTo.current.getElementsByClassName('section-content')[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);
    }, [componentInEditMode]);

    // Load template data and available containers when component mounts
    useEffect(() => {
        if (id) {
            fetchTemplateData();
        }
    }, [id]);

    useEffect(() => {
        if (autoSaveEnabled && isLayoutDirty) {
            autoSave(layout);
        }
    }, [autoSaveEnabled]);

    useEffect(() => {
        if (layoutToAutoSave) {
            handleSave(layoutToAutoSave, true);
        }
    }, [layoutToAutoSave])

    useEffect(() => {
        if (!isAutoSaving && needToAutoSaveAgain.current) {
            needToAutoSaveAgain.current = false;
            autoSave(layout);
        }
    }, [isAutoSaving])

    // Fetch template data from the API
    const fetchTemplateData = () => {
        setLoading(true);
        axios.all([
            CheckService.getTemplate(id, useGlobalEndpoint),
            CheckService.getTemplateLayout(id, useGlobalEndpoint),
            CheckService.getTemplateOptions(),
            CheckService.getTemplateMedia(id, useGlobalEndpoint)
        ]).then(axios.spread((settingsResponse, layoutResponse, optionsResponse, mediaResponse) => {
            setTemplate(settingsResponse);
            setTemplateOptions(optionsResponse);
            setTemplateMedia(mediaResponse);
            if ((settingsResponse.type === TemplateTypes.GLOBAL && canEditGlobal) || settingsResponse.type === TemplateTypes.CUSTOM) {
                CheckService.getTemplateVersions(id, useGlobalEndpoint).then((versions) => {
                    setIsLatestVersion(isLatestEditableVersion(versions, settingsResponse.versionNumber));
                }).catch(() => {
                    NotificationToaster.show(
                        Intent.WARNING,
                        "Could not load version history"
                    );
                    setIsLatestVersion(false);
                });
            };

            // Set status color based on template status
            switch (settingsResponse.status) {
                case "Draft":
                    setStatusColour("E87C0D");
                    break;
                case "Published":
                    setStatusColour("0E9245");
                    break;
                case "Archived":
                    setStatusColour("E25241");
                    break;
                default:
                    setStatusColour("");
                    break;
            }

            if (layoutResponse && layoutResponse.pages.length > 0) {
                setLayout(assignSectionNumbersToAllComponents(layoutResponse));
                setOriginalLayout(assignSectionNumbersToAllComponents(HelperFunctions.deepClone({ ...layoutResponse })));
            } else if (!isLayoutDirty) {
                setAutoSaveEnabled(true);
            }
            setLoading(false);
        }),
            (error) => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Could not retrieve template, please try again"
                );
                setLoading(false);
            }
        );
    };

    const mediaMap = useMemo(() => {
        const map = {};
        for (const img of templateMedia) {
            map[img.mediaReference] = img;
        }
        return map;
    }, [templateMedia]);

    const isLatestEditableVersion = (versions, currentVersion) => {
        if (!versions || versions.length === 0) return false;

        const versionNumbers = versions
            .filter(v =>
                v.status.key === TemplateStatuses.DRAFT ||
                v.status.key === TemplateStatuses.PUBLISHED
            )
            .map(v => v.versionNumber);

        return currentVersion === Math.max(...versionNumbers);
    };

    const assignSectionNumbersToAllComponents = (newLayout) => {
        const allComponents = flattenLayout(newLayout);
        let currentQuestionNumber = 1;
        let currentSectionNumber = 1;
        let currentPageNumber = 1;

        allComponents.forEach(c => {
            let componentNumber = 0;

            if (isQuestionComponent(c)) {
                componentNumber = currentQuestionNumber;
                ++currentQuestionNumber;
            } else if (isPageComponent(c)) {
                componentNumber = currentPageNumber;
                ++currentPageNumber;
            } else {
                componentNumber = currentSectionNumber;
                ++currentSectionNumber;
            }

            c.componentNumber = componentNumber;
        });

        return newLayout;
    }

    const onLayoutUpdate = (newLayout) => {
        setLayout(assignSectionNumbersToAllComponents(newLayout));
        setComponentInEditMode(null);
    };

    // Navigate back to template listing
    const handleClose = () => {
        navigate(baseRoute);
    };

    // Save current template changes including layout
    const handleSave = (layoutToSave, onlyMapIds) => {
        if (onlyMapIds) {
            setIsAutoSaving(true);
        } else {
            setSaving(true);
        }

        // Save the layout
        const layoutClone = HelperFunctions.deepClone(layoutToSave);
        const allComponents = flattenLayout(layoutClone);
        const allTriggerQuestionIds = allComponents.filter(x => !!x.condition)
            .map(x => x.condition.triggerQuestionId);

        // Map the trigger question ids to the server version if one exists
        allComponents.filter(c => c.condition?.triggerQuestionId && c.condition.triggerQuestionId < 0)
            .forEach(c => {
                const triggerQuestion = allComponents.find(a => a.id === c.condition.triggerQuestionId);
                const triggerQuestionServerId = internalReferencesToServerIdsMap.current[triggerQuestion.internalReference];

                if (triggerQuestionServerId) {
                    c.condition.triggerQuestionId = triggerQuestionServerId;
                }
            })

        // Map the component ids to either be the server ID, or null if not used as a trigger question.
        allComponents.filter(c => c.id < 0)
            .forEach(c => {
                const serverId = internalReferencesToServerIdsMap.current[c.internalReference];

                if (serverId) {
                    c.id = serverId
                } else if (!isQuestionComponent(c) || !allTriggerQuestionIds.includes(c.id)) {
                    c.id = null
                } else {
                    // Need to keep negative ID as used for a trigger question
                }
            });



        CheckService.saveTemplateLayout(id, layoutClone, useGlobalEndpoint)
            .then(() => {
                return CheckService.updateTemplateMedia(id, addedMedia, removedMedia, useGlobalEndpoint)
                    .catch((error) => {
                        NotificationToaster.show(Intent.DANGER, "Failed to update template media");
                        setSaving(false);
                        throw error;
                    })
            })
            .then(() => {
                setLastSaveTime(Date.now());
                setSaving(false);
                if (!onlyMapIds) {
                    NotificationToaster.show(
                        Intent.SUCCESS,
                        "Layout saved successfully"
                    );
                }

                // After successful save, fetch the updated layout to get server-assigned IDs
                if (onlyMapIds) {
                    fetchNewComponentIdsAfterAutoSaving(layoutToSave);
                } else {
                    setIsAutoSaving(false);
                    fetchTemplateData();
                }
            })
            .catch((error) => {
                setSaving(false);
                NotificationToaster.show(Intent.DANGER, "Failed to save layout");
            });
    };

    const fetchNewComponentIdsAfterAutoSaving = (layoutToSave) => {
        setOriginalLayout(HelperFunctions.deepClone(layoutToSave));
        CheckService.getTemplateLayout(id, useGlobalEndpoint)
            .then(newLayout => {
                const newMap = {};
                flattenLayout(newLayout)
                    .forEach(l => newMap[l.internalReference] = l.id);

                internalReferencesToServerIdsMap.current = newMap;
                setIsAutoSaving(false);
            })
    }

    const autoSave = (layoutToSave) => {
        if (layout.pages.length === 0
            || loading
            || saving
            || !autoSaveEnabled

        ) {
            return;
        }

        if (isAutoSaving) {
            needToAutoSaveAgain.current = true;
            return;
        }

        setLayoutToAutoSave(layoutToSave);
    };

    // Add a new section - supports proper nesting
    const addSection = ({ parentSectionId = null, refQuestionId = null, refSectionId = null }) => {
        // Create a new section object following the API's expected format
        // Assign a temporary ID for UI tracking, but mark it as new
        const newSection = {
            $componentType: "StandardContainer",
            id: nextUniqueId, // Temporary ID for UI tracking
            title: 'New Section',
            childComponents: [],
            isNew: true,
            internalReference: uuidv4()
        };

        addNewComponentToLayout({
            parentSectionId,
            refQuestionId,
            refSectionId,
            component: newSection,
        });
    };

    const addQuestion = ({ questionType, parentSectionId, refQuestionId = null, refSectionId = null }) => {
        const newQuestion = addBlankQuestionType(questionType);

        addNewComponentToLayout({
            parentSectionId,
            refQuestionId,
            refSectionId,
            component: newQuestion,
        });
    }

    const addBlankQuestionType = (questionType) => {
        return {
            $componentType: `${questionType}Question`,
            title: "",
            id: nextUniqueId,
            internalReference: uuidv4()
        };
    }

    const addNewComponentToLayout = ({ component, parentSectionId = null, refQuestionId = null, refSectionId = null, refPageId = null }) => {
        setOldLayout(HelperFunctions.deepClone(layout));
        const layoutClone = { ...layout };

        if (!parentSectionId) {
            if (refPageId) {

                includeComponentNextTo({
                    childComponents: layoutClone.pages,
                    referenceId: refPageId,
                    newComponent: component
                });

            } else if (refSectionId) {

                const allComponents = flattenLayout(layoutClone);
                const parent = allComponents.find(p => p.childComponents?.some(c => c.id === refSectionId && isSectionOrPageComponent(c)));

                includeComponentNextTo({
                    childComponents: parent.childComponents,
                    referenceId: refSectionId,
                    newComponent: component
                });

            } else {
                updateComponentTitle({ comp: component, containerList: layoutClone.pages });
                layoutClone.pages.push(component);
            }
        } else {
            const allComponents = flattenLayout(layoutClone);
            const parent = allComponents.find(l => l.id === parentSectionId && isSectionOrPageComponent(l));

            if (refQuestionId || refPageId) {

                includeComponentNextTo({
                    childComponents: parent.childComponents,
                    referenceId: refQuestionId || refPageId,
                    newComponent: component
                });

            } else {
                updateComponentTitle({ comp: component, containerList: parent.childComponents });
                parent.childComponents.push(component);
            }
        }

        onLayoutUpdate(layoutClone);
        setComponentInEditMode(component);

        function includeComponentNextTo({ childComponents, referenceId, newComponent }) {
            updateComponentTitle({ comp: newComponent, containerList: childComponents });
            const siblingIndex = childComponents.findIndex(c => c.id === referenceId);
            childComponents.splice(siblingIndex + 1, 0, newComponent);
        }

        function updateComponentTitle({ comp, containerList }) {
            let count = 0;

            // Pages
            if (!comp.$componentType) {
                count = containerList.filter(c => !c.$componentType).length;
                // Sections
            } else if (comp.$componentType === 'StandardContainer') {
                count = containerList.filter(c => c.$componentType === 'StandardContainer').length;
                // Questions
            } else {
                return;
            }

            comp.title = count ? `${comp.title} ${count + 1}` : comp.title;
        }
    }

    // Add a new page container to the template
    const addPage = ({ questionId = null, sectionId = null, pageId = null } = {}) => {
        // Create a new page object following the API's expected format
        const newPage = {
            id: nextUniqueId,
            title: 'Page',
            childComponents: [],
        };

        let refPageId = pageId;
        const refComponentId = questionId || sectionId;
        if (refComponentId) {
            refPageId = layout.pages.find(pageItem => pageItem.childComponents.some(c => c.id === refComponentId))?.id || null;
        }

        // Add the new page
        addNewComponentToLayout({
            refPageId,
            component: newPage,
        });
        setShowAddPageConfirm(false);
    };

    const removeComponent = () => {
        const layoutClone = HelperFunctions.deepClone(layout);
        const allComponents = flattenLayout(layoutClone);

        if (!parentOfComponentToDelete) {
            layoutClone.pages = layoutClone.pages.filter(pageItem => pageItem.id !== componentToDelete.id);
        } else {
            const parent = allComponents.find(c => c.id === parentOfComponentToDelete.id && isSectionOrPageComponent(c));
            parent.childComponents = parent.childComponents.filter(c => c.id !== componentToDelete.id || c.$componentType !== componentToDelete.$componentType);
        }

        for (const componentWithInvalidCondition of componentsWhereTriggerQuestionIsWithinDeletableComponent) {
            const componentClone = allComponents.find(c => c.id === componentWithInvalidCondition.id && c.$componentType === componentWithInvalidCondition.$componentType);
            componentClone.condition = null;
        }

        onLayoutUpdate(layoutClone);
        autoSave(layoutClone);
        setComponentToDelete(null);
    };

    const cancelDelete = () => {
        setComponentToDelete(null);
    };

    const showConfirmationBoxForDeletingComponent = setComponentToDelete;

    const archiveTemplate = () => {
        setShowArchiveTemplateConfirm(false);
        setArchiving(true);
        CheckService.setTemplateStatus(id, { status: TemplateStatuses.ARCHIVED }, useGlobalEndpoint)
            .then(() => {
                setArchiving(false);
                NotificationToaster.show(
                    Intent.SUCCESS,
                    "Template archived successfully"
                );

                fetchTemplateData();
            })
            .catch((error) => {
                NotificationToaster.show(Intent.DANGER, "Failed to archive template");
            })
            .finally(() => {
                setArchiving(false);
            });
    }

    const createNewVersion = () => {
        setShowNewVersionConfirm(false);
        setVersioning(true);
        CheckService.createNewVersion(id, useGlobalEndpoint)
            .then((newVersionId) => {
                setVersioning(false);
                NotificationToaster.show(
                    Intent.SUCCESS,
                    "New version created successfully"
                );
                navigate(`${baseRoute}/${newVersionId}`);
            })
            .catch((error) => {
                NotificationToaster.show(Intent.DANGER, "Failed to create new version");
            })
            .finally(() => {
                setVersioning(false);
            });
    }

    const publishTemplate = () => {
        setShowPublishTemplateConfirm(false);
        setSaving(true);
        CheckService.setTemplateStatus(id, { status: TemplateStatuses.PUBLISHED }, useGlobalEndpoint)
            .then(() => {
                setSaving(false);
                NotificationToaster.show(
                    Intent.SUCCESS,
                    "Template published successfully"
                );

                fetchTemplateData();
            })
            .catch((error) => {
                NotificationToaster.show(Intent.DANGER, "Failed to published template");
            })
            .finally(() => {
                setSaving(false);
            });
    }

    const getReasonWhyUserCannotChangeStatusOfTemplate = (action, ...requiredCurrentStatuses) => {
        if (template.relatedEventType && action === 'archive') {
            return "This template is linked to an event type and cannot be archived.";
        }

        if (template.type === TemplateTypes.GLOBAL && !userIsSuperInternal) {
            return `You do not have the required access to ${action} the template.`
        }

        if (!!componentInEditMode && !isReadOnly) {
            return `Cannot ${action} template while a ${getTypeOfComponent(componentInEditMode)} is being edited.`;
        }

        if (!requiredCurrentStatuses.includes(template.status)) {
            return `Cannot ${action} template as its currently not set to the right status.`
        }

        if (isLayoutDirty) {
            return `Please save layout before you can ${action} the template.`
        }

        return null;
    }

    const ButtonRow = () => (
        <div className="inline-items inline-items-space">
            <div className="button-row">
                <LinkButton
                    text="TEMPLATE SETTINGS"
                    href={`${baseRoute}/${id}/settings`}
                    disabled={(!!componentInEditMode && !isReadOnly) || isAutoSaving}
                    loading={showLoadingState}
                    intent={Intent.PRIMARY}
                />
                <Tooltip content={getReasonWhyUserCannotChangeStatusOfTemplate('archive', TemplateStatuses.DRAFT, TemplateStatuses.PUBLISHED)}>
                    <Button
                        text="ARCHIVE"
                        onClick={() => setShowArchiveTemplateConfirm(true)}
                        loading={showLoadingState}
                        intent={Intent.PRIMARY}
                        icon="archive"
                        disabled={!!getReasonWhyUserCannotChangeStatusOfTemplate('archive', TemplateStatuses.DRAFT, TemplateStatuses.PUBLISHED) || isAutoSaving}
                    />
                </Tooltip>
                <Button
                    text="NEW VERSION"
                    onClick={() => setShowNewVersionConfirm(true)}
                    loading={showLoadingState}
                    intent={Intent.PRIMARY}
                    icon="new-drawing"
                    disabled={(!!componentInEditMode && !isReadOnly) || template.status !== TemplateStatuses.PUBLISHED || !isLatestVersion || isLayoutDirty || isAutoSaving}
                />
                <Tooltip content={getReasonWhyUserCannotChangeStatusOfTemplate('publish', TemplateStatuses.DRAFT)}>
                    <Button
                        text="Publish"
                        onClick={() => setShowPublishTemplateConfirm(true)}
                        loading={showLoadingState}
                        icon="export"
                        intent={Intent.PRIMARY}
                        disabled={!!getReasonWhyUserCannotChangeStatusOfTemplate('publish', TemplateStatuses.DRAFT) || isAutoSaving}
                    />
                </Tooltip>

                <Button
                    text="Preview"
                    onClick={() => setPreviewOpen(true)}
                    disabled={showLoadingState || (!!componentInEditMode && !isReadOnly)}
                    loading={showLoadingState}
                    intent={Intent.PRIMARY}
                    icon="lab-test"
                />
            </div>
            <div className="inline-items inline-items-space">
                <ShowHide
                    evaluator={!isReadOnly}
                    show={<AutoSave
                        isDisabled={saving || loading || isAutoSaving}
                        defaultChecked={autoSaveEnabled}
                        lastAutoSaveTime={lastSaveTime}
                        forceShowSaveTime={true}
                        onAutoSaveChange={setAutoSaveEnabled}
                    />}
                />

                <div className="button-row">
                    <ShowHide
                        evaluator={isReadOnly}
                        hide={<ButtonSave
                            text="SAVE"
                            onClick={() => handleSave(layout)}
                            disabled={saving || isAutoSaving}
                            loading={showLoadingState}
                            simpleDisabled={!!componentInEditMode}
                            intent={Intent.NONE}
                        />}
                    />
                    <Button
                        text="CLOSE"
                        onClick={handleClose}
                        disabled={saving || (!!componentInEditMode && !isReadOnly) || isAutoSaving}
                        loading={showLoadingState}
                    />
                </div>
            </div>
        </div>
    );

    const renderTitleForComponentToDelete = () => {
        if (!componentToDelete) {
            return "";
        }

        let componentIdentifier = componentToDelete.title;

        if (!componentIdentifier) {
            componentIdentifier = `${getTypeNameForComponent(componentToDelete)} ${componentToDelete.componentNumber}`;
        }

        return `Delete ${componentIdentifier}`;
    }

    const renderTitleForComponentToClone = () => {
        if (!componentToClone) {
            return "";
        }

        let componentIdentifier = componentToClone.title;

        if (!componentIdentifier) {
            componentIdentifier = `${getTypeNameForComponent(componentToClone)} ${componentToClone.componentNumber}`;
        }

        return `Duplicate ${componentIdentifier}`;
    }

    const getTypeNameForComponent = (component) => {
        if (isPageComponent(component)) {
            return "Page";
        } else if (isQuestionComponent(component)) {
            return "Question";
        } else {
            return "Section";
        }
    }

    const renderComponent = (component, parent, index) => {
        if (isSectionOrPageComponent(component)) {
            return renderSection(component, parent, index);
        } else {
            return renderQuestion(component, parent, index);
        }
    }

    const getTriggerQuestion = (component) => {
        if (!component.condition) {
            return null;
        }

        return flattenedLayout.find(q => isQuestionComponent(q) && q.id === component.condition.triggerQuestionId);
    }

    const renderQuestion = (question, parent, index) => {
        const helperImagesUrls = (question.helperImages || [])
            .map((mediaReference) => mediaMap[mediaReference])
            .filter(Boolean);

        return <div
            key={`question-${question.id}`}
            ref={componentInEditMode === question ? scrollTo : null}>
            <HierarchyDroppableContainer
                parentComponent={parent}
                index={index}
                hide={question === componentBeingMoved || isPageComponent(componentBeingMoved)}
            />
            <HierarchyQuestion
                settings={question}
                questionNumber={question.componentNumber}
                disabled={saving || archiving || versioning || (!!componentInEditMode && componentInEditMode !== question)}
                loading={loading}
                onEdit={() => onEdit(question)}
                onSave={(settings) => onComponentSave(question, settings)}
                onClone={() => onCloningComponent(question)}
                onDelete={() => showConfirmationBoxForDeletingComponent(question)}
                onClear={revertToOldLayout}
                onAddMedia={onAddMedia}
                onAddPage={() => addPage({ questionId: question.id })}
                onAddSection={() => addSection({ parentSectionId: parent.id, refQuestionId: question.id })}
                onAddQuestion={(questionType) => addQuestion({ questionType, parentSectionId: parent.id, refQuestionId: question.id })}
                questionTypes={questionTypes}
                inEditMode={componentInEditMode === question}
                reasonWhyComponentCannotBeRepeatable={reasonWhyComponentCannotBeRepeatable}
                pottentialTriggerQuestions={pottentialTriggerQuestions}
                currentTriggerQuestion={getTriggerQuestion(question)}
                conditionalHelperText={conditionalHelperText}
                helperImages={helperImagesUrls}
                conditionsWhereThisIsTheTrigger={getConditionsWhereQuestionIsTrigger(question, flattenedLayout)}
                isItemBeingMoved={componentBeingMoved === question}
                readOnly={isReadOnly}
            />
        </div>;
    }

    const cloneComponent = () => {
        const layoutClone = { ...layout };
        const parentComponentChildren = getParentComponentsChildren(componentToClone, layoutClone);
        const indexOfChild = parentComponentChildren.findIndex(c => c.id === componentToClone.id && c.$componentType === componentToClone.$componentType);
        const clonedComponent = reassignIdsForClonedComponent(HelperFunctions.deepClone(componentToClone));

        clonedComponent.title = nameOfClonedComponent;
        clonedComponent.internalReference = uuidv4();
        clearHelperImagesDeep(clonedComponent);

        parentComponentChildren.splice(indexOfChild + 1, 0, clonedComponent);
        onLayoutUpdate(layoutClone);
        autoSave(layoutClone);
        setComponentToClone(null);
    }

    const onCloningComponent = (component) => {
        setComponentToClone(component);
        setNameOfClonedComponent(component.title);
    }

    const clearHelperImagesDeep = (component) => {
        if (component.helperImages) {
            component.helperImages = null;
        }
        if (component.childComponents && Array.isArray(component.childComponents)) {
            component.childComponents.forEach(clearHelperImagesDeep);
        }
    };

    const reassignIdsForClonedComponent = (component) => {
        const flattenedComponents = flattenComponent(component);
        let currentIndex = 0;

        for (const flattened of flattenedComponents) {
            const conditionsWhereThisIsTheTrigger = getConditionsWhereQuestionIsTrigger(flattened, flattenedComponents);
            flattened.id = nextUniqueId - currentIndex;
            flattened.internalReference = uuidv4();
            conditionsWhereThisIsTheTrigger.forEach(c => c.triggerQuestionId = flattened.id);
            currentIndex++;
        }

        return component;
    }

    const onEdit = (component) => {
        setOldLayout(HelperFunctions.deepClone(layout));
        setComponentInEditMode(component);
    }

    const onComponentSave = (component, newSettings) => {
        if (isReadOnly) {
            setComponentInEditMode(null);
            return;
        }

        const layoutClone = HelperFunctions.deepClone(layout);
        const parentComponentChildren = getParentComponentsChildren(component, layoutClone);
        const indexOfChild = parentComponentChildren.findIndex(c => c.id === component.id && c.$componentType === component.$componentType);

        parentComponentChildren[indexOfChild] = { ...component, ...newSettings };

        if (conditionsWhereTriggerQuestionIsCurrentEditableComponent.length) {
            removeAnyOrphanedTriggerAnswers(layoutClone, newSettings);
        }

        onLayoutUpdate(layoutClone);
        autoSave(layoutClone);
        setComponentInEditMode(null);
    }

    const removeAnyOrphanedTriggerAnswers = (layoutClone, newSettings) => {
        if (componentInEditMode.$componentType === "YesNoQuestion") {
            // Cannot remove answers for yes,no question
            return;
        }

        const flattenedClone = flattenLayout(layoutClone);

        for (const condition of conditionsWhereTriggerQuestionIsCurrentEditableComponent) {
            const conditionalComponent = flattenedLayout.find(c => c.condition === condition);
            const conditionalComponentClone = flattenedClone.find(c => c.id === conditionalComponent.id && c.$componentType === conditionalComponent.$componentType);

            conditionalComponentClone.condition.requiredAnswers = condition.requiredAnswers
                .filter(a => newSettings.options.map(o => o.id).includes(a));

            if (!conditionalComponentClone.condition.requiredAnswers.length) {
                conditionalComponentClone.condition = null;
            }
        }
    }

    const getParentComponentsChildren = (component, layoutClone) => {
        const allComponents = flattenLayout(layoutClone);
        const parent = getParentComponent(component, allComponents)

        return parent ? parent.childComponents : layoutClone.pages;
    }

    const getParentComponent = (component, allComponentsClone) => {
        return isPageComponent(component) ?
            null :
            allComponentsClone.find(s => isSectionOrPageComponent(s) && s.childComponents.some(c => areComponentsEqual(c, component)))
    }

    const onAddMedia = async (mediaReference, file) => {
        try {
            return await CheckService.uploadTemplateMedia(id, mediaReference, file, useGlobalEndpoint);
        } catch {
            NotificationToaster.show(
                Intent.DANGER,
                "Failed to upload media. Please try again."
            );
            return null;
        }
    };

    // Recursive section rendering component
    const renderSection = (section, parent, index) => {
        return <div
            key={`section-${section.componentNumber}`}
            ref={componentInEditMode === section ? scrollTo : null}>
            <HierarchyDroppableContainer
                parentComponent={parent}
                index={index}
                hide={componentBeingMoved === section || (isPageComponent(componentBeingMoved) && !isPageComponent(section))}
            />
            <HierarchySection
                section={section}
                disabled={saving || archiving || versioning || (componentInEditMode && componentInEditMode !== section)}
                loading={loading}
                isPage={isPageComponent(section)}
                onEdit={() => onEdit(section)}
                onSave={(_, settings) => onComponentSave(section, settings)}
                onClone={() => onCloningComponent(section)}
                onDelete={() => showConfirmationBoxForDeletingComponent(section)}
                onPageAdd={() => addPage({ sectionId: !isPageComponent(section) ? section.id : null, pageId: isPageComponent(section) ? section.id : null })}
                onSectionAdd={(addInnerSection = true) => {

                    const parentSectionId = addInnerSection ? section.id : null;
                    const refSectionId = addInnerSection ? null : section.id;

                    addSection({ parentSectionId, refSectionId })
                }}
                onQuestionAdd={(questionType, addInnerSection = true) => {

                    const parentSectionId = addInnerSection ? section.id : null;
                    const refSectionId = addInnerSection ? null : section.id;

                    addQuestion({ questionType, parentSectionId, refSectionId })
                }}
                onClear={revertToOldLayout}
                questionTypes={questionTypes}
                inEditMode={componentInEditMode === section}
                reasonWhyComponentCannotBeRepeatable={reasonWhyComponentCannotBeRepeatable}
                pottentialTriggerQuestions={pottentialTriggerQuestions}
                currentTriggerQuestion={getTriggerQuestion(section)}
                conditionalHelperText={conditionalHelperText}
                hideAddComponentButtons={!!componentBeingMoved}
                isItemBeingMoved={componentBeingMoved === section}
                readOnly={isReadOnly}
            >
                <Fragment>
                    {/* Render child sections recursively if any */}
                    <ShowHide
                        key={`section-children-${section.id}`}
                        evaluator={!!section.childComponents}
                        show={section.childComponents.map((c, i) => renderComponent(c, section, i))}
                    />
                    {<HierarchyDroppableContainer
                        parentComponent={section}
                        index={section.childComponents?.length ?? 0}
                        hide={isPageComponent(componentBeingMoved)}
                    />}
                </Fragment>
            </HierarchySection>
        </div>
    };

    const renderConditionWarningForDeletingComponent = () => <ShowHide
        evaluator={!!componentsWhereTriggerQuestionIsWithinDeletableComponent.length}
        show={<p>
            <span className="bold">Please note:</span> removing this {getTypeOfComponent(componentToDelete)} will also remove {componentsWhereTriggerQuestionIsWithinDeletableComponent.length} condition(s) as it {componentToDelete && isQuestionComponent(componentToDelete) ? "is a trigger" : "contains trigger questions"}.
        </p>}
    />

    const onMovingItem = (event) => {
        const movedItemId = event.active?.id;

        if (!movedItemId) {
            setComponentBeingMoved(null);
            return;
        }

        let component = null;

        if (movedItemId.startsWith('question-')) {
            const questionId = +movedItemId.replace('question-', '');
            component = flattenedLayout.find(l => l.id === questionId && isQuestionComponent(l));
        } else {
            const sectionId = +movedItemId.replace('section-', '');
            component = flattenedLayout.find(l => l.id === sectionId && isSectionOrPageComponent(l));
        }

        setComponentBeingMoved(component);
    }

    const onFinishMovingItem = (event) => {
        if (event.over) {
            const splitId = event.over.id.split(':');
            const parentId = +splitId[0];
            const index = +splitId[1];

            moveComponent(parentId, index);
        } else {
            setComponentBeingMoved(null);
        }
    }

    const moveComponent = (parentId, index) => {
        const clonedLayout = HelperFunctions.deepClone(layout);
        const allClonedComponents = flattenLayout(clonedLayout);
        const parentClone = allClonedComponents.find(c => isSectionOrPageComponent(c) && c.id === parentId);
        const previousParent = getParentComponent(componentBeingMoved, allClonedComponents);

        const componentBeingMovedClone = allClonedComponents.find(c => areComponentsEqual(c, componentBeingMoved));
        const previousParentsChildren = previousParent ? previousParent.childComponents : clonedLayout.pages;

        const indexOfComponentBeingMoved = previousParentsChildren.findIndex(c => areComponentsEqual(c, componentBeingMovedClone));

        if (parentClone === previousParent || (!parentClone && !previousParent)) {
            moveItemInParent(previousParentsChildren, index, indexOfComponentBeingMoved);
        } else {
            // Remove the component from where it was previously
            previousParentsChildren.splice(indexOfComponentBeingMoved, 1);

            // Now add it to where it should be now
            parentClone.childComponents.splice(index, 0, componentBeingMovedClone);
        }

        const invalidComponentsAfterMove = getInvalidComponentsAfterMove(flattenLayout(clonedLayout));

        if (invalidComponentsAfterMove.length) {
            setOldLayout(layout);
            setComponentsWithInvalidConditionsAfterMove(invalidComponentsAfterMove);
        } else {
            setComponentBeingMoved(null);
            autoSave(clonedLayout);
        }

        onLayoutUpdate(clonedLayout);
    }

    const getInvalidComponentsAfterMove = (newComponents) => {
        const componentsWithConditions = newComponents.filter(x => x.condition);
        const invalidComponents = [];

        for (const componentWithCondition of componentsWithConditions) {
            const validTriggerQuestionsForComponent = getPottentialTriggerQuestions(componentWithCondition, newComponents);

            if (!validTriggerQuestionsForComponent.some(q => q.id === componentWithCondition.condition.triggerQuestionId)) {
                invalidComponents.push(componentWithCondition);
            }
        }

        return invalidComponents;
    }

    const moveItemInParent = (children, toIndex, fromIndex) => {
        if (toIndex > fromIndex) {
            toIndex--;
        }

        const deletedElement = children.splice(fromIndex, 1)[0];
        children.splice(toIndex, 0, deletedElement);
    }

    const areComponentsEqual = (componentOne, componentTwo) => componentOne.id === componentTwo.id && componentOne.$componentType === componentTwo.$componentType;

    const cancelMove = () => {
        setComponentBeingMoved(null);
        setComponentsWithInvalidConditionsAfterMove([]);
        setLayout(oldLayout);
    }

    const completeMove = () => {
        const clonedLayout = HelperFunctions.deepClone(layout);
        const allComponents = flattenLayout(clonedLayout);
        const componentsToRemoveConditions = allComponents
            .filter(c => componentsWithInvalidConditionsAfterMove.some(i => areComponentsEqual(c, i)));

        componentsToRemoveConditions.forEach(c => c.condition = null);

        setLayout(clonedLayout);
        setComponentBeingMoved(null);
        setComponentsWithInvalidConditionsAfterMove([]);
        autoSave(clonedLayout);
    }

    const revertToOldLayout = () => {
        if (oldLayout) {
            setLayout(HelperFunctions.deepClone(oldLayout));
            setOldLayout(null);
        }

        setComponentInEditMode(null);
    }

    const HierarchyDroppableContainer = ({ parentComponent, index, hide }) => {
        const idPrefix = parentComponent?.id ?? "";

        const { setNodeRef } = useDroppable({
            id: `${idPrefix}:${index}`,
        });

        const shouldShowDroppableContainer = () => {
            if (hide || !componentBeingMoved || parentComponent === componentBeingMoved) {
                return false;
            }

            return !!parentComponent || isPageComponent(componentBeingMoved);
        }

        return <ShowHide
            evaluator={shouldShowDroppableContainer()}
            show={<div ref={setNodeRef} className="hierarchy-drop hierarchy-drop-item-container spacer-bottom-small">
                Drop {componentBeingMoved && getTypeNameForComponent(componentBeingMoved)}
            </div>}
        />
    }

    const prevScroll = useRef(0);

    const handleScroll = () => {
        const topScroll = document.documentElement.scrollTop || document.body.scrollTop;
        const topScrollWithOffset = topScroll - getHiddenElementsHeight();
        const scrollDirection = topScrollWithOffset > prevScroll.current ? 'down' : 'up';
        const distance = Math.round(Math.abs(topScrollWithOffset - prevScroll.current));
        const MIN_SCROLL_DISTANCE = 150;

        // Ignore small scrolls
        if (distance < MIN_SCROLL_DISTANCE) {
            // In case the window doesn't have scroll enough for the min distance
            if (topScroll === 0 || (topScroll < (MIN_SCROLL_DISTANCE / 2) && scrollDirection === 'up')) {
                document.body.removeAttribute("data-scrolling");
            }
            return;
        }

        if (scrollDirection === 'down') {
            document.body.dataset.scrolling = "true";
        } else if (scrollDirection === 'up') {
            document.body.removeAttribute("data-scrolling");
        }

        prevScroll.current = topScrollWithOffset;
    };

    function getHiddenElementsHeight() {
        return Array.from(document.querySelectorAll('.hidden-on-scroll'))
            .reduce((sum, el) => {
                const style = window.getComputedStyle(el);
                return style.opacity !== "0" && style.display !== "none"
                    ? sum + el.offsetHeight
                    : sum;
            }, 0);
    }

    useEffect(() => {
        document.addEventListener('scroll', handleScroll);
        return () => {
            document.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <UserAccess
            perform={requiredAction}
            yes={() => {
                return (
                    <Fragment>
                        {/* Sticky header section */}
                        <div className="template-builder-sticky-header">
                            {/* Breadcrumb navigation */}
                            <div className="row template-builder-breadcrumbs hidden-on-scroll">
                                <Breadcrumbs items={props.breadcrumbs} />
                            </div>

                            {/* Page title */}
                            <div className="row template-builder-page-title hidden-on-scroll">
                                <h1 className="no-margin">Template Builder</h1>
                            </div>

                            <div className="row spacer-bottom template-builder-notification hidden-on-scroll">
                                <NotificationInline
                                    allowClose={false}
                                    show={template.type === 'Custom' && template.subCategory === "WalkaroundCheck" && template.status === TemplateStatuses.DRAFT}
                                    text="By building a custom walkaround check template, you might not include items that DVSA recommend are checked as part of a vehicle walkaround check. Doing so may result in the vehicle being operated in an unroadworthy condition. It is recommended you use the Logistics UK standard templates."
                                    intent="warning"
                                ></NotificationInline>
                            </div>

                            {/* Navigation buttons */}
                            <div className="row spacer-bottom-small spacer-top">
                                <ButtonRow />
                            </div>

                            {/* Template information display */}
                            <div className="row spacer-bottom template-builder-info hidden-on-scroll">
                                <div>
                                    <span className="semi-bold">Template Name:</span>{" "}
                                    <StringToEllipsesString length={80}>{template.name}</StringToEllipsesString>
                                    <span className="spacer-left">
                                        <span className="semi-bold">Version:</span>{" "}
                                        <span>{template.versionNumber}</span>
                                    </span>
                                    <span className="spacer-left">
                                        <span className="semi-bold">Status:</span>{" "}
                                        <span>{template.status}</span>{" "}
                                        {statusColour && (
                                            <CircleStatus
                                                background={statusColour}
                                                tooltip={template.status}
                                            />
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Scrollable content area */}
                        <div className="template-builder-content">
                            {/* Template builder interface - using recursive section rendering */}
                            <DndContext
                                onDragEnd={onFinishMovingItem}
                                onDragStart={onMovingItem}
                            >
                                <div className="row spacer-bottom">
                                    <div className="spacer-bottom-small">
                                        {layout.pages.map((pageItem, i) => renderComponent(pageItem, null, i))}
                                        {<HierarchyDroppableContainer
                                            parentComponent={null}
                                            index={layout.pages.length}
                                            hide={false}
                                        />}
                                    </div>
                                </div>
                            </DndContext>

                            {/* Add new dashed box for ADD PAGE */}
                            <ShowHide
                                evaluator={!componentBeingMoved && !isReadOnly}
                                show={<div className="row spacer-top spacer-bottom-large hierarchy-add-child-container">
                                    <button
                                        className={classNames("hierarchy-drop notification-panel hierarchy-add-child", { 'bp6-skeleton': showLoadingState })}
                                        onClick={() => setShowAddPageConfirm(true)}
                                        onKeyDown={(e) =>
                                            e.key === "Enter" || e.key === " "
                                                ? setShowAddPageConfirm(true)
                                                : null
                                        }
                                        tabIndex={0}
                                        aria-label="Add new page"
                                        type="button"
                                        disabled={saving || archiving || versioning}
                                    >
                                        <span>ADD PAGE</span>
                                    </button>
                                </div>}
                            />
                        </div>

                        {/* Confirmation dialog for adding a page */}
                        <AlertConfirm
                            title="Add new page"
                            isOpen={showAddPageConfirm}
                            onConfirm={addPage}
                            onCancel={() => setShowAddPageConfirm(false)}
                            confirmButtonIntent="primary"
                            confirmButtonText="Add Page"
                        >
                            <p>Are you sure you want to add a new page to the template?</p>
                        </AlertConfirm>

                        <AlertConfirm
                            title={`Invalidate Condition${componentsWithInvalidConditionsAfterMove?.length > 1 ? 's' : ''}`}
                            isOpen={!!componentsWithInvalidConditionsAfterMove?.length}
                            onConfirm={() => completeMove()}
                            onCancel={() => cancelMove()}
                            confirmButtonIntent="danger"
                            confirmButtonText={"Move " + getTypeOfComponent(componentBeingMoved)}
                        >
                            <p>Moving this {getTypeOfComponent(componentBeingMoved)} will invalidate and remove {componentsWithInvalidConditionsAfterMove.length > 1 ? `${componentsWithInvalidConditionsAfterMove.length} conditions` : '1 condition'}. Are you sure you want to move this {getTypeOfComponent(componentBeingMoved)}?</p>
                        </AlertConfirm>

                        {/* Confirmation dialog for deleting a page */}
                        <AlertConfirm
                            title={renderTitleForComponentToDelete()}
                            isOpen={componentToDelete !== null}
                            onConfirm={() => removeComponent()}
                            onCancel={cancelDelete}
                        >
                            <ShowHide
                                evaluator={!!componentToDelete && isSectionOrPageComponent(componentToDelete)}
                                show={<>
                                    <p>
                                        Are you sure you want to delete this {renderTitleForComponentToDelete().toLocaleLowerCase()}? Any child sections or
                                        questions will also be removed.
                                    </p>
                                    {renderConditionWarningForDeletingComponent()}
                                </>
                                }
                                hide={<>
                                    <p>
                                        Are you sure you want to delete this question?
                                    </p>
                                    {renderConditionWarningForDeletingComponent()}
                                </>}
                            />

                        </AlertConfirm>

                        <AlertConfirm
                            title={renderTitleForComponentToClone()}
                            isOpen={componentToClone !== null}
                            onConfirm={() => cloneComponent()}
                            onCancel={() => setComponentToClone(null)}
                            confirmButtonIntent="primary"
                            confirmButtonText="Duplicate"
                            disabled={!!componentToClone && !nameOfClonedComponent && (!isSectionOrPageComponent(componentToClone) || isPageComponent(componentToClone))}
                        >
                            <FormTextInput
                                headingText={`Enter a header for the duplicate question ${getTypeOfComponent(componentToClone)}:`}
                                onChange={(event) => setNameOfClonedComponent(event.target.value)}
                                value={nameOfClonedComponent ?? ""}
                                id={'clone-component-text-input'}
                                maxLength={500}
                            />

                        </AlertConfirm>

                        {/* Confirmation dialog for archiving a template */}
                        <AlertConfirm
                            title="Archive template"
                            isOpen={showArchiveTemplateConfirm}
                            onConfirm={archiveTemplate}
                            onCancel={() => setShowArchiveTemplateConfirm(false)}
                            confirmButtonIntent="primary"
                            confirmButtonText="Archive Template">
                            <p>Are you sure you want to archive the template?</p>
                        </AlertConfirm>

                        {/* Confirmation dialog creating a new version of a template */}
                        <AlertConfirm
                            title="New Version"
                            isOpen={showNewVersionConfirm}
                            onConfirm={createNewVersion}
                            onCancel={() => setShowNewVersionConfirm(false)}
                            confirmButtonIntent="primary"
                            confirmButtonText="Create New Version">
                            <p>Are you sure you want to create a new version of the template?</p>
                        </AlertConfirm>

                        <AlertConfirm
                            title="Publish template"
                            isOpen={showPublishTemplateConfirm}
                            onConfirm={publishTemplate}
                            onCancel={() => setShowPublishTemplateConfirm(false)}
                            confirmButtonIntent="primary"
                            confirmButtonText="Publish Template">

                            <NotificationInline
                                allowClose={false}
                                show={template.type === 'Custom' && template.subCategory === "WalkaroundCheck"}
                                text="By building a custom walkaround check template, you might not include items that DVSA recommend are checked as part of a vehicle walkaround check. Doing so may result in the vehicle being operated in an unroadworthy condition. It is recommended you use the Logistics UK standard templates."
                                intent="warning"
                            ></NotificationInline>

                            <p>Are you sure you want to publish the template? Publishing the template will allow users to complete checks against it and will lock down the template so no further edits can be made.</p>
                        </AlertConfirm>

                        <AlertUnsavedData
                            isDirty={isLayoutDirty}>
                        </AlertUnsavedData>

                        <TemplateBuilderPreview
                            onClose={() => setPreviewOpen(false)}
                            layout={layout}
                            template={template}
                            show={previewOpen}
                            templateOptions={templateOptions}
                            mediaMap={mediaMap}
                        />
                    </Fragment>
                );
            }}
            no={() => <PageRestricted />}
        />
    );
}

TemplateBuilder.propTypes = {
    breadcrumbs: PropTypes.array,
    useGlobalConfig: PropTypes.bool,
};
